import React, {useCallback, useState} from 'react';
import {useRouter} from 'next/router';

import {useUpdateActionPlan} from '#apollo/schema';
import {Button} from '#component/base';
import {ModalRoot} from '#component/base/Modal';
import {ActionPlanActivatedModal} from '#component/modal/ActionPlanActivatedModal';
import {ActionPlanPage} from '#component/page/ActionPlan/ActionPlan.types';
import {EventType, useAnalytics, UserActionEvent} from '#component/provider/AnalyticsProvider';
import {CONTACT_URL} from '#constant/app';
import {sprinkles} from '#style/sprinkles';

import {actionPlanPageQuery} from '../../useActionPlan';

type ButtonState = ActionPlanPage.State | 'hidden';

interface ButtonContent {
  id: string;
  label: string;
  variant: 'primary' | 'secondary';
  action?: () => void;
}

interface ButtonsProps {
  buttonState: ButtonState;
  actionPlanId?: string;
  isActionPlanActive: boolean;
  canCheckIn?: boolean;
}

export function Buttons({
  buttonState,
  actionPlanId,
  isActionPlanActive,
  canCheckIn = false,
}: ButtonsProps) {
  const {track} = useAnalytics();
  const {push} = useRouter();
  const [confirmationModalOpen, setConfimationModalOpen] = useState(false);
  const [updateActionPlan, {loading}] = useUpdateActionPlan({
    refetchQueries: [{query: actionPlanPageQuery}],
  });

  const handleActivateActionPlanClick = useCallback(async () => {
    if (actionPlanId) {
      const {data} = await updateActionPlan({
        variables: {
          input: {
            id: actionPlanId,
            active: true,
          },
        },
      });
      if (data) {
        setConfimationModalOpen(true);
        track<UserActionEvent>({
          evtName: EventType.UserAction,
          actionName: 'Action Plan Activated',
        });
      }
    }
  }, [actionPlanId, updateActionPlan, track]);

  const useButtons = useCallback(
    (buttonState: ButtonState): ButtonContent[] => {
      switch (buttonState) {
        case 'inactive':
          return [
            {
              id: 'activate',
              label: 'Activate Plan',
              variant: 'primary',
              action: handleActivateActionPlanClick,
            },
            {
              id: 'customize',
              label: 'Customize Plan',
              variant: 'secondary',
              action: () => push('actionplan/edit'),
            },
          ];
        case 'active':
        case 'high-performer':
          return [
            {
              id: 'checkin',
              label: canCheckIn ? 'Check-in' : 'View Check-In Summary',
              variant: 'primary',
              action: () => push('checkin'),
            },
            {
              id: 'customize',
              label: 'Customize Plan',
              variant: 'secondary',
              action: () => push('actionplan/edit'),
            },
          ];
        case 'error':
          return [
            {
              id: 'support',
              label: 'Contact Support',
              variant: 'primary',
              action: () => push(CONTACT_URL),
            },
          ];
        default:
          return [];
      }
    },
    [canCheckIn, handleActivateActionPlanClick, push],
  );

  const buttons = useButtons(buttonState);
  return (
    <>
      {buttons.length > 0 && (
        <>
          <div
            className={sprinkles({
              display: 'flex',
              flexDirection: ['col', 'col', 'row'],
              content: ['center', 'center', 'between'],
              mL: isActionPlanActive ? 0 : 'auto',
              pX: isActionPlanActive ? 0 : [24, 24, 0],
              pT: [24, 24, 0],
              width: ['full', 'full', 'maxContent'],
            })}>
            {buttons.map(({id, label, variant, action}, index) => (
              <Button
                key={index}
                className={sprinkles({
                  width: ['full', 'full', 'maxContent'],
                  mT: [12, 12, 0],
                  mR: [0, 0, 12],
                })}
                variant={variant}
                onClick={action}
                disabled={loading}
                loading={id === 'activate' ? loading : false}>
                {label}
              </Button>
            ))}
          </div>
          <ModalRoot open={confirmationModalOpen}>
            <ActionPlanActivatedModal />
          </ModalRoot>
        </>
      )}
    </>
  );
}
